/**
 * Language Column Selector Component for Review Page
 * 
 * Allows users to show/hide specific language columns in the translation review table.
 * Supports both source and target language management.
 */

import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Languages,
  Check,
  Eye,
  EyeOff,
  ChevronDown,
  Settings,
  RotateCcw
} from 'lucide-react'
import Button from '../ui/Button'

const LanguageColumnSelector = ({ 
  languageInfo, 
  onLanguageToggle, 
  onShowAll, 
  onHideAll,
  onResetToDefault,
  className = '' 
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  const {
    sourceLanguage,
    targetLanguages = [],
    allLanguages = [],
    visibleLanguages = [],
    hiddenLanguages = []
  } = languageInfo || {}

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!allLanguages || allLanguages.length === 0) {
    return null
  }

  const isLanguageVisible = (language) => visibleLanguages.includes(language)
  const isSourceLanguage = (language) => language === sourceLanguage

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2"
      >
        <Languages className="w-4 h-4" />
        <span>{t('review.languageSelector.title')}</span>
        <div className="flex items-center space-x-1 text-xs">
          <span className="text-green-600 dark:text-green-400">
            {visibleLanguages.length}
          </span>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600 dark:text-gray-400">
            {allLanguages.length}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
          {/* Header with bulk actions */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <Settings className="w-4 h-4 mr-2" />
                {t('review.languageSelector.columnVisibility')}
              </h3>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {visibleLanguages.length} {t('review.languageSelector.of')} {allLanguages.length} {t('review.languageSelector.visible')}
              </div>
            </div>
            
            {/* Bulk action buttons */}
            <div className="flex space-x-2">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  onShowAll()
                  setIsOpen(false)
                }}
                className="flex items-center text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                {t('review.languageSelector.showAll')}
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  onHideAll()
                  setIsOpen(false)
                }}
                className="flex items-center text-xs"
                disabled={visibleLanguages.length <= 1}
              >
                <EyeOff className="w-3 h-3 mr-1" />
                {t('review.languageSelector.hideAll')}
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  onResetToDefault()
                  setIsOpen(false)
                }}
                className="flex items-center text-xs"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                {t('review.languageSelector.reset')}
              </Button>
            </div>
          </div>

          {/* Language List */}
          <div className="max-h-64 overflow-y-auto">
            {/* Source Language Section */}
            {sourceLanguage && (
              <div className="p-2">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">
                  {t('review.languageSelector.sourceLanguage')}
                </div>
                <div
                  className="flex items-center justify-between p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => onLanguageToggle(sourceLanguage)}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      {isLanguageVisible(sourceLanguage) ? (
                        <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                      ) : null}
                    </div>
                    <span className="text-sm text-gray-900 dark:text-white font-medium">
                      {sourceLanguage}
                    </span>
                    <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1 py-0.5 rounded">
                      {t('review.languageSelector.source')}
                    </span>
                  </div>
                  <div className="flex items-center">
                    {isLanguageVisible(sourceLanguage) ? (
                      <Eye className="w-4 h-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Target Languages Section */}
            {targetLanguages.length > 0 && (
              <div className="p-2 border-t border-gray-100 dark:border-gray-700">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">
                  {t('review.languageSelector.targetLanguages')} ({targetLanguages.length})
                </div>
                <div className="space-y-1">
                  {targetLanguages.map((language) => (
                    <div
                      key={language}
                      className="flex items-center justify-between p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => onLanguageToggle(language)}
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          {isLanguageVisible(language) ? (
                            <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                          ) : null}
                        </div>
                        <span className="text-sm text-gray-900 dark:text-white">
                          {language}
                        </span>
                      </div>
                      <div className="flex items-center">
                        {isLanguageVisible(language) ? (
                          <Eye className="w-4 h-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Other Languages Section (if any) */}
            {allLanguages.some(lang => lang !== sourceLanguage && !targetLanguages.includes(lang)) && (
              <div className="p-2 border-t border-gray-100 dark:border-gray-700">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">
                  {t('review.languageSelector.otherColumns')}
                </div>
                <div className="space-y-1">
                  {allLanguages
                    .filter(lang => lang !== sourceLanguage && !targetLanguages.includes(lang))
                    .map((language) => (
                    <div
                      key={language}
                      className="flex items-center justify-between p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => onLanguageToggle(language)}
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          {isLanguageVisible(language) ? (
                            <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                          ) : null}
                        </div>
                        <span className="text-sm text-gray-900 dark:text-white">
                          {language}
                        </span>
                      </div>
                      <div className="flex items-center">
                        {isLanguageVisible(language) ? (
                          <Eye className="w-4 h-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer with summary */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 rounded-b-lg">
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {hiddenLanguages.length > 0 ? (
                <span>
                  {t('review.languageSelector.hiddenLanguages', { count: hiddenLanguages.length })}: {' '}
                  <span className="font-mono">{hiddenLanguages.slice(0, 3).join(', ')}</span>
                  {hiddenLanguages.length > 3 && '...'}
                </span>
              ) : (
                <span className="text-green-600 dark:text-green-400">
                  {t('review.languageSelector.allVisible')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LanguageColumnSelector